<script setup lang="ts">
import RcShipmentSearchCard from '@/components/rc/shipments/RcShipmentSearchCard.vue'
import { OrderBy } from '@/types/shared/filters'
import type { IShipmentFilters } from '@/types/rc/filters'
import { ShipmentType } from '@/types/rc/shipments'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref, watch } from 'vue'
import { getAllShipments } from '@/services/rc/shipments.service'

const props = withDefaults(
  defineProps<{
    type?: ShipmentType
    workOrderNumber?: string
    customer?: number
    withContainers?: boolean
    disabled?: boolean
    placeholder?: string
  }>(),
  {
    type: undefined,
    workOrderNumber: undefined,
    customer: undefined,
    disabled: false,
    withContainers: undefined,
    placeholder: 'ID or tracking data',
  }
)

// When it's coming from the options, we've got a full IDashboardShipment.
// When it's coming from the model, we just have a number. Hence the narrowing.
// We can't not have it and support presetting the select without fetching options.
interface IResourceIdentifier {
  id: number
}

// Model shipment. Interface with parent. Single number, or undefined.
const selectedShipment = defineModel<IResourceIdentifier | undefined>()

// Watch for changes in the model shipment, and update the options so they contain a proper label for the one preset.
watch(selectedShipment, (shipment) => {
  // If it's being cleared, make sure it's set to undefined (not null).
  if (!shipment) {
    selectedShipment.value = undefined
    return
  }

  // If there's no options yet (this is being preset), set the mock values for proper render.
  // Options will be overwritten by the search method as soon as the user positions the cursor there.
  if (!shipmentOptions.value.length) {
    shipmentOptions.value.push({ value: shipment, label: `Shipment #${shipment.id}` })
  }

  // If there's options, find the option and set it as selected.
  // This is in case the model shipment is just { id: number } and not the full object.
  const preSelectedOption = shipmentOptions.value.find((option) => option.value.id === shipment.id)
  selectedShipment.value = preSelectedOption?.value ?? shipment
})

const shipmentSearchLoading = ref<boolean>(false)
const shipmentOptions = ref<OptionType[]>([])

/**
 * Gets shipment options for a work order.
 *
 * @param query optional query (used for search).
 */
const searchShipments = async (query?: string) => {
  const trimmedQuery = query?.trim()

  try {
    shipmentSearchLoading.value = true

    const searchParams: IShipmentFilters = {
      page: 1,
      per_page: 10,
      work_order_number: props.workOrderNumber,
      customer_id: props.customer,
      type: props.type,
      has_containers: props.withContainers,
      order_by: 'id',
      order_by_order: OrderBy.DESC,
    }

    if (trimmedQuery) {
      searchParams.fragment = trimmedQuery
    }

    const shipments = await getAllShipments(searchParams)
    shipmentOptions.value = shipments.data.map((shipment) => {
      return {
        label: `Shipment #${shipment.id}`,
        value: shipment,
      }
    })

    // If there is a selected shipment preset, replace it (so far an IResourceIdentifier) with the actual one (a superset).
    if (!selectedShipment.value) return
    const selectedShipmentOption = shipments.data.find((option) => option.id === selectedShipment.value!.id)
    selectedShipment.value = selectedShipmentOption ?? selectedShipment.value
  } catch {
    ElMessage.error({ message: `Failed to ${query ? 'search' : 'populate'} shipments.`, grouping: true })
    shipmentOptions.value = []
  } finally {
    shipmentSearchLoading.value = false
  }
}
</script>

<template>
  <el-select-v2
    v-model="selectedShipment"
    :options="shipmentOptions"
    filterable
    remote
    :remote-method="searchShipments"
    clearable
    :loading="shipmentSearchLoading"
    no-data-text="No shipments found"
    :placeholder="props.placeholder"
    :item-height="96"
    value-key="id"
    :disabled="disabled"
  >
    <template #default="{ item }">
      <RcShipmentSearchCard :shipment="item.value" />
    </template>
    <template #loading>
      <div class="remote-search--loading">
        <Icon icon="eos-icons:three-dots-loading" height="20" /></div
    ></template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.remote-search--loading {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
